<template>
  <div class="tw-mt-6">
    <div class="tw-p-4 tw-bg-white tw-rounded tw-border tw-border-gray-200 tw-mb-6">
      <h1 class="tw-text-2xl tw-font-bold tw-text-primary-700">{{ session?.name || 'Session Title' }}</h1>

      <div class="tw-flex tw-items-center tw-gap-3 tw-text-xs tw-text-gray-500 tw-my-2">
        <span class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:calendar-bold" class="tw-text-gray-400" />
          {{ formattedSessionDate }}
        </span>
        <span class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:clock-circle-bold" class="tw-text-gray-400" />
          {{ formattedDuration }}
        </span>
        <span v-if="isSessionNotStarted" class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:clock-circle-bold" class="tw-text-primary-500" />
          <span class="tw-text-primary-600 tw-font-medium">Not Started</span>
        </span>
        <span v-else-if="isSessionEnded" class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:check-circle-bold" class="tw-text-green-500" />
          <span class="tw-text-green-600 tw-font-medium">Session Ended</span>
        </span>
        <span v-else-if="session?.is_live" class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:play-circle-bold" class="tw-text-red-500" />
          <span class="tw-text-red-600 tw-font-medium">Live Now</span>
        </span>
        <span v-else-if="session?.status" class="tw-flex tw-items-center tw-gap-1">
          <icon icon="solar:info-circle-bold" class="tw-text-gray-400" />
          {{ session.status }}
        </span>
      </div>

      <p class="tw-text-gray-600 tw-my-4">{{ session?.description || 'No description available.' }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import moment from 'moment'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  isSessionNotStarted: {
    type: Boolean,
    default: false
  },
  isSessionEnded: {
    type: Boolean,
    default: false
  }
})

// Computed properties for formatting
const formattedSessionDate = computed(() => {
  if (!props.session?.start_date) return 'Date not specified'
  
  try {
    const dateObj = moment(props.session.start_date)
    const today = moment()
    const tomorrow = moment().add(1, 'day')

    if (dateObj.isSame(today, 'day')) {
      return `Today ${dateObj.format('h:mm A')}`
    } else if (dateObj.isSame(tomorrow, 'day')) {
      return `Tomorrow ${dateObj.format('h:mm A')}`
    } else {
      return dateObj.format('MMM D, h:mm A')
    }
  } catch (error) {
    console.error('Error formatting session date:', error)
    return 'Date not specified'
  }
})

const formattedDuration = computed(() => {
  if (!props.session?.start_date || !props.session?.end_date) return 'Duration not specified'
  
  try {
    const start = moment(props.session.start_date)
    const end = moment(props.session.end_date)
    const duration = moment.duration(end.diff(start))
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  } catch (error) {
    console.error('Error calculating duration:', error)
    return 'Duration not specified'
  }
})
</script>
